<template>
  <div
    ref="actionRootRef"
    v-show="!hidden && !hiddenByGroup && effectiveVisible"
    :title="tooltip || data.description?._text"
    :data-platform-control="actionControlName"
    class="action"
    :class="classes"
  >
    <MLabel
      v-if="noButton"
      :icon="data.image?._text"
      :label="displayLabel"
      is-link
      @click="(e: MouseEvent) => onActivate(e)"
    />
    <MButton
      v-else
      severity="primary"
      :outlined="isXmlOutlined"
      :disabled="!isEnabled"
      @click="(e: MouseEvent) => onActivate(e)"
    >
      <MLabel :icon="icon || data.image?._text" :label="displayLabel" />
    </MButton>
  </div>
</template>

<script setup lang="ts">
import { useActions } from '@/common/composables/useActions'
import { useDialog } from '@/common/composables/useDialog'
import { usePageData } from '@/common/composables/usePageData'
import MButton from '@/components/ui/MButton.vue'
import MLabel from '@/components/ui/MLabel.vue'
import {
  type ActionContext,
  actionService,
  getActionDialogClosedScript,
} from '@/services/action/action.service'
import { rememberTooltipAnchor } from '@/services/messages/tooltip-popup'
import {
  beginSlotAction,
  endSlotAction,
  isSlotActionInFlight,
} from '@/services/form-events/slotActionInFlight'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, onUnmounted, ref, unref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { resolveTilesRowScopedRegistryName } from '@/common/utils/resolveTilesRowScopedRegistryName'
import { resolveTilesInlineTargetVisible } from '@/common/utils/tilesSetInlineVisibility'
import type { SwingTilesRowScopeLike } from '@/common/utils/tilesRowPanelData'
import {
  resolveActionLayoutLevelText,
  resolveActionLayoutTextId,
} from '@/services/action/actionScriptRuntime'
import {
  resolveActionInitialFlag,
  resolveActionInitialTitle,
} from '@/common/utils/resolveActionPageState'
import { extractActionScriptText, extractActionTextValue } from '@/services/action/normalizeActionTransfer'
import { isActionOutlineColor } from '@/common/utils/actionOutlineColor'
import { configService } from '@/common/utils/clientConfig'
import {
  FormProviderKey,
  GetFormKey,
  GetPageDataKey,
  OpenUrls,
  ReloadElementKey,
  RunCodeKey,
  RegisterActionKey,
  SwingTilesActionVisibilityMapKey,
  SwingTilesHasInlineAfterLoadKey,
  SwingTilesInlineVisibilityTargetsKey,
  SwingTilesRowScopeKey,
  UnRegisterActionKey,
} from './injection-keys'
import type { ActionData, ActionHooks } from './types'
import { useAlerts, useFormStates } from '@/common/composables'
import { usePageErrors } from '@/common/composables/usePageError'

interface Props {
  data: ActionData
  title?: string
  noButton?: boolean
  elementId?: string | null
  registrable?: boolean
  hidden?: boolean
  inGroup?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  noButton: false,
  elementId: null,
  registrable: false,
  hidden: false,
  inGroup: false,
})

const emit = defineEmits<{
  actionEnd: []
}>()

// Inject из глобального провайдера
const panelId = inject<string>('panelId', '')
const getForm = inject<({ formId, panelId, elementId }: any) => any>(GetFormKey, () => ({}))
const getPageData = inject<({ formId, pageId }: any) => any>(GetPageDataKey, () => ({}))
const openUrls = inject(OpenUrls, () => {})
const dialogConfigInjected = inject<any>('dialogConfig', null)

const dialogConfig = computed(() => {
  const v = dialogConfigInjected
  if (v == null) return null
  return unref(v)
})
const registerAction = inject(RegisterActionKey, () => {})
const unRegisterAction = inject(UnRegisterActionKey, () => {})
const reloadElement = inject<(elementName: string, data: any, actionContext?: any) => Promise<any>>(
  ReloadElementKey,
  async () => {},
)
const formProvider = inject(FormProviderKey, null)
const runCode = inject<(cmd: string, selectedData?: any) => void>(RunCodeKey, () => {})
const { pageData, pageDataSlot, pageRegistryFormId, pageWindowInstanceId, pageActions, pageStates } = usePageData()
const appStore = useAppStore()
const { currentPlatform } = storeToRefs(appStore)
const router = useRouter()
const route = useRoute()
const actions = useActions()
const dialog = useDialog()
const pageErrors = usePageErrors()
const formStates = useFormStates()
const alerts = useAlerts()
const swingTilesRowScope = inject(SwingTilesRowScopeKey, null)
const tilesActionVisibilityMap = inject(SwingTilesActionVisibilityMapKey, null)
const tilesHasInlineAfterLoad = inject(SwingTilesHasInlineAfterLoadKey, null)
const tilesInlineVisibilityTargets = inject(SwingTilesInlineVisibilityTargetsKey, null)

const actionIdForInitialState = props.data._attributes ? +props.data._attributes.id : 0
const actionNameForInitialState = (() => {
  const raw = props.data.name
  if (raw == null) return ''
  if (typeof raw === 'string') return raw.trim()
  return String(raw._text ?? '').trim()
})()
const actionControlName = computed(() => actionNameForInitialState || undefined)

const actionRegistryName = computed(() => {
  return resolveTilesRowScopedRegistryName(swingTilesRowScope, props.data.name?._text)
})

const persistedScopedVisibility = computed(() => {
  const registryName = actionRegistryName.value
  if (!registryName || !formProvider?.getPersistedScopedVisibility) return undefined
  return formProvider.getPersistedScopedVisibility(registryName)
})

// Начальная видимость из jsResult.states — до processStates/setTimeout.
// В строке tiles не берём persisted scoped — он мог остаться false до registerAction (WUI-408).
const isVisible = ref(
  swingTilesRowScope
    ? resolveActionInitialFlag(
        pageStates.value,
        actionIdForInitialState,
        actionNameForInitialState,
        'is_visible',
        true,
      )
    : persistedScopedVisibility.value !== undefined
      ? persistedScopedVisibility.value
      : resolveActionInitialFlag(
          pageStates.value,
          actionIdForInitialState,
          actionNameForInitialState,
          'is_visible',
          true,
        ),
)

/** Row-local override из MTiles.setInlineVisibility (inlineAfterLoad per-row). */
const effectiveVisible = computed(() => {
  const actionName = actionNameForInitialState
  if (swingTilesRowScope && actionName) {
    const slice = swingTilesRowScope.rowActionVisibility?.value
    const sliceVisible =
      slice && Object.prototype.hasOwnProperty.call(slice, actionName)
        ? slice[actionName]
        : undefined
    const override = resolveTilesInlineTargetVisible({
      elementName: actionName,
      rowIndex: swingTilesRowScope.rowIndex,
      hasInlineAfterLoad: Boolean(tilesHasInlineAfterLoad?.value),
      targets: tilesInlineVisibilityTargets?.value,
      rowMap: tilesActionVisibilityMap?.value,
      sliceVisible,
      getterVisible: swingTilesRowScope.getActionVisibility?.(actionName),
      persistedVisible: persistedScopedVisibility.value,
    })
    if (override !== undefined) return override
  }
  return isVisible.value
})

/** Синхронизировать isVisible при row-local override (setVisible registry + v-show). */
watch(
  effectiveVisible,
  (visible) => {
    if (swingTilesRowScope && actionNameForInitialState && isVisible.value !== visible) {
      isVisible.value = visible
    }
  },
  { immediate: true },
)

const isEnabled = ref(
  resolveActionInitialFlag(
    pageStates.value,
    actionIdForInitialState,
    actionNameForInitialState,
    'is_enabled',
    true,
  ),
)
const tooltip = ref('')
const icon = ref('')
const actionTitle = ref<string | null>(
  resolveActionInitialTitle(
    pageStates.value,
    actionIdForInitialState,
    actionNameForInitialState,
  ) ?? null,
)
const actionRootRef = ref<HTMLElement | null>(null)

const displayLabel = computed(() => {
  const fromXml = (props.data.title?._text ?? '').trim()
  if (actionTitle.value != null) return actionTitle.value
  if (props.title) return props.title
  return fromXml
})

// Computed properties
const windowType = computed(() => props.data.window__type?._text || '')
// Числовой id формы для контекста действий
const formId = computed(() => {
  if (pageRegistryFormId.value) {
    const n = parseInt(pageRegistryFormId.value, 10)
    return Number.isFinite(n) ? n : 0
  }
  const d = pageData.value as any
  const rid = pageDataSlot.value || (route.params?.id != null && String(route.params.id) !== '' ? String(route.params.id) : '')
  const resolved = resolveElementRegistryFormId(d, rid)
  const n = parseInt(resolved, 10)
  return Number.isFinite(n) ? n : 0
})
const layout = computed(() => props.data.layout || {})
const level = computed(() => layout.value.level?._text || 'form')
const position = computed(() => layout.value.position?._text || '')
const hiddenByGroup = computed(() => {
  if (props.inGroup) return false
  return !!dropDownActionName.value && !dropDownMain.value
})
const selectedData = computed(() => {
  if (props.data.selected?.fields) return props.data.selected
  // Tiles: сырая строка только для PK в createClientDataUniData, не весь dump row.
  const rowData = swingTilesRowScope?.getRowData?.()
  if (rowData && typeof rowData === 'object' && !Array.isArray(rowData)) {
    return rowData
  }
  return null
})

// Context для действия TODO: оформить как ActionContext!
const ctx = computed(() => {
  const layoutId = layout.value.id?._text || layout.value.id
  // TODO: причем тут pageId???
  const pageId = level.value === 'page' ? layoutId : undefined

  return {
    selectedData: selectedData.value,
    id: props.data._attributes ? +props.data._attributes.id : 0,
    name: props.data.name?._text || '',
    formId: formId.value,
    // TODO: зачем на panelId, elementId, pageId (что это вообще)?
    panelId: panelId,
    elementId: props.elementId,
    pageId: pageId,
    level: level.value,
    data: props.data,
    dialogConfig: dialogConfig.value,
  }
})

const hooks = computed(
  (): ActionHooks => ({
    onActivate: onActivate,
    getAction: () => ({
      doAction: () => {
        onActivate()
      },
    }),
    setTitle: setTitle,
    setToolTipText: setToolTipText,
    setVisible: setVisible,
    setEnable: setEnable,
    setEnabled: setEnable,
    setIcon: setIcon,
    getVisualContainer: () => ({
      setTitle: setTitle,
      setToolTipText: setToolTipText,
      setVisible: setVisible,
      setEnable: setEnable,
      setEnabled: setEnable,
      setIcon: setIcon,
    }),
  }),
)

const buttonGroup = computed(() => props.data.buttonGroup?._text || '')
const dropDownActionName = computed(() => {
  const dropDown = props.data.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text || ''
  }
  return dropDown?._text || ''
})
const dropDownMain = computed(() => {
  const dropDown = props.data.dropDownMain
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text === 'true'
  }
  return dropDown?._text === 'true'
})

const isTruthyFlag = (value: any): boolean => {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  if (value == null) return false
  const text = String(value).trim().toLowerCase()
  return text === 'true' || text === '1' || text === 'yes' || text === 'y'
}

const isXmlOutlined = computed(
  () => configService.isSpd() && isActionOutlineColor(props.data?.color),
)

const classes = computed(() => ({
  position_move: buttonGroup.value === 'position_move',
  'action--xml-outlined': isXmlOutlined.value,
}))

const onActivate = async (event?: MouseEvent) => {
  const eventTarget = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null
  const control = eventTarget?.closest('[data-platform-control]')
  rememberTooltipAnchor(
    control instanceof HTMLElement ? control : actionRootRef.value,
    actionNameForInitialState,
  )
  const slotId = String(pageDataSlot.value ?? '').trim()
  // A second click while SAME_WINDOW is still replacing the core closes the
  // session the first response is about to show ("Форма не актуальна").
  if (slotId && isSlotActionInFlight(slotId)) {
    return
  }
  if (slotId) beginSlotAction(slotId)
  try {
    const platform = props.data.platform?._text || currentPlatform.value || undefined

    // Формируем контекст действия TODO: убрать лишнее
    const layout = props.data?.layout || {}
    const layoutLevel = resolveActionLayoutLevelText(layout) || ctx.value.level
    const layoutId = resolveActionLayoutTextId(layout)
    const pageId = layoutLevel === 'page' && layoutId ? layoutId : ctx.value.pageId
    const hostFormId =
      pageRegistryFormId.value ||
      (formId.value ? String(formId.value) : '') ||
      pageDataSlot.value ||
      ''

    const actionContext: ActionContext = {
      id: ctx.value.id.toString(),
      name: ctx.value.name,
      elementId: (layoutLevel === 'element' && layoutId) ? String(layoutId) : (ctx.value.elementId || undefined),
      panelId: ctx.value.panelId,
      formId: hostFormId || undefined,
      pageId: pageId?.toString(),
      level:
        (layoutLevel as 'form' | 'panel' | 'element' | 'page') ||
        (ctx.value.level as 'form' | 'panel' | 'element' | 'page'),
      data: props.data,
      platform,
      windowType: windowType.value,
      onClick: extractActionScriptText(props.data.onClick) || undefined,
      confirm: extractActionScriptText(props.data.confirm) || undefined,
      transfer: extractActionTextValue(props.data.transfer) as string | undefined,
      transferType: extractActionTextValue(props.data.transferType) as string | undefined,
      event: {
        dialogClosed: getActionDialogClosedScript(props.data as Record<string, any>),
        chainCalled: extractActionScriptText(props.data.event?.chainCalled) || undefined,
      },
      flow: extractActionScriptText(props.data.flow) || undefined,
      selectedData: selectedData.value,
      doActionForAdjacentWindow: isTruthyFlag(
        extractActionTextValue(props.data.doActionForAdjacentWindow),
      ),
      openInNewWindowFromModifier: Boolean(event?.altKey),
      tilesRowScope: swingTilesRowScope ?? undefined,
    }

    const getFormWithTilesScope = (params: {
      formId?: string | number
      panelId?: string | number
      elementId?: string | number
      pageDataSlot?: string
      flatten?: boolean
      tilesRowScope?: SwingTilesRowScopeLike | null
    } = {}) =>
      getForm({
        ...params,
        tilesRowScope: params.tilesRowScope ?? swingTilesRowScope ?? undefined,
      })

    // Выполняем действие через actionService
    const actionPromise = actionService.executeAction(actionContext, {
      appStore,
      router,
      actions,
      dialog,
      reloadElement,
      refillElement: formProvider?.refillElement,
      openUrls,
      pageErrors,
      formStates,
      alerts,
      getForm: getFormWithTilesScope,
      getPageData,
      getTableSelection: formProvider?.getTableSelection,
      runCode,
      formProvider,
      pageData: pageData.value,
      pageDataSlot: pageDataSlot.value,
      pageRegistryFormId: pageRegistryFormId.value,
      pageWindowInstanceId: pageWindowInstanceId.value,
    })
    formProvider?.trackPendingPlatformAction?.(actionPromise)
    await actionPromise

    emit('actionEnd')
  } catch (error) {
    console.error('[MAction] Error executing action:', error)
    emit('actionEnd')
  } finally {
    if (slotId) endSlotAction(slotId)
  }
}

const setVisible = (value: boolean) => {
  isVisible.value = value
}

const setEnable = (value: boolean) => {
  isEnabled.value = value
}

const setToolTipText = (text: string) => {
  tooltip.value = text
}

const setTitle = (text: string) => {
  const value = text == null ? '' : String(text)
  actionTitle.value = value
  const id = actionIdForInitialState || actionNameForInitialState
  formProvider?.persistActionTitle?.(id, actionNameForInitialState, value)
}

const setIcon = (iconObj: any) => {
  icon.value = iconObj.url?.split('/').pop() || ''
}

// Lifecycle
onMounted(() => {
  if (props.registrable) {
    registerAction(
      {
        ...ctx.value,
        registryName: actionRegistryName.value || undefined,
      },
      hooks.value,
    )
  }
})

onUnmounted(() => {
  if (props.registrable) {
    unRegisterAction({
      ...ctx.value,
      registryName: actionRegistryName.value || undefined,
    })
  }
})
</script>

<style scoped lang="scss">
.action {
  display: inline-flex;
  align-items: center;

  &.position_move {
    ::v-deep(.p-button) {
      margin: 0;
    }
  }
}

::v-deep(.p-button) {
  text-transform: none !important;
  min-width: auto !important;
  padding: 8px 16px !important;
  margin: 0.075rem 0.05rem;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  .p-button-label {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 6px !important;
  }
}

.menu-activator {
  margin: 0;
  border-radius: 0;
  margin-left: -2px;
}
</style>
